/**
 * Hands a blob to the browser's own save dialog.
 *
 * FRONTEND_DESIGN §9: "Offer explicit copy/download actions; do not silently
 * write exports to arbitrary paths." This is the frontend half of that, and
 * the server half is that `POST /export` has no destination field at all — so
 * the only path the archive can reach is one the founder chose here, in the
 * browser's own dialog, which this code cannot see or influence.
 *
 * The object URL is revoked immediately after the click. It is a handle to a
 * complete copy of everything the installation holds, held by the document;
 * leaving it alive would mean any later script running on this origin could
 * fetch the whole export back out of it, and it would survive a navigation
 * within the SPA because nothing else revokes it.
 */

export interface SaveFileOptions {
  readonly blob: Blob;
  readonly fileName: string;
}

export function saveFile({ blob, fileName }: SaveFileOptions): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    // The name the server proposed. `download` also forces the navigation to
    // be a download rather than a render, which matters for a media type the
    // browser might otherwise decide it can display.
    anchor.download = fileName;
    // Appended before clicking: Firefox ignores a click on an anchor that is
    // not in the document.
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
